import React from "react";

import { css, styled } from "@compiled/react";

// @ts-ignore
import AkItem from "@atlaskit/item";
import { token as akToken } from "@atlaskit/tokens";

import { Centered } from "../centered/centered";

const tablet = 850;

interface CardContainerProps {
    color?: CardColor;
}
export const CardContainer = styled.div({
    backgroundColor: ({ color = "light" }: CardContainerProps) =>
        color === "light"
            ? akToken("elevation.surface", "#FFFFFF")
            : akToken("color.background.accent.gray.subtlest", "#F4F5F7"),
    borderRadius: akToken("border.radius.100", "3px"),
    padding: akToken("space.200", "16px"),
    [`&:not(:last-child)`]: {
        marginBottom: akToken("space.150", "12px")
    }
});

const MainDataContainer = styled.div({
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    [`@media (min-width: ${tablet}px)`]: {
        flexWrap: "nowrap"
    }
});

const SpaceSeparator = styled.div({
    marginTop: akToken("space.200", "16px")
});

const LineSeparator = styled.div({
    height: akToken("space.025", "2px"),
    borderRadius: akToken("border.radius.100", "3px"),
    backgroundColor: akToken("color.background.accent.gray.subtler", "#DFE1E6"),
    margin: `${akToken("space.200", "16px")} 0`
});

type CardColor = "light" | "dark";

export interface CardProps {
    href?: string;
    icon?: React.ReactNode;
    mainContent?: React.ReactNode;
    mainContentDescription?: React.ReactNode;
    isExternalLink?: boolean;
    actions?: React.ReactNode;
    details?: React.ReactNode;
    detailsSeparator?: "line" | "space";
    color?: CardColor;
    testId?: string;
    onClick?(): void;
}

export const InertCard: React.FC<{ children: React.ReactNode }> = ({
    children
}) => <Centered>{children}</Centered>;

export class Card extends React.Component<CardProps> {
    public static defaultProps = {
        color: "light",
        detailsSeparator: "line"
    };

    public override render() {
        const {
            href,
            icon,
            mainContent,
            actions,
            onClick,
            color,
            mainContentDescription,
            testId
        } = this.props;

        const linkComponent = InertCard;

        return (
            <CardContainer
                data-test-id={testId}
                data-testid={testId}
                color={color}
                css={css({
                    "zIndex": 100,
                    "boxShadow": akToken(
                        "elevation.shadow.raised",
                        `0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)`
                    ),
                    "@supports (-ms-ime-align: auto)": {
                        boxShadow: akToken(
                            "elevation.shadow.raised",
                            `0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.13), 1px 0 1px rgba(9, 30, 66, 0.13), -1px 0 1px rgba(9, 30, 66, 0.13), 0 -1px 1px rgba(9, 30, 66, 0.13)`
                        )
                    },
                    "@media screen and (-ms-high-contrast: none)": {
                        boxShadow: akToken(
                            "elevation.shadow.raised",
                            `0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.13), 1px 0 1px rgba(9, 30, 66, 0.13), -1px 0 1px rgba(9, 30, 66, 0.13), 0 -1px 1px rgba(9, 30, 66, 0.13)`
                        )
                    }
                })}
            >
                <MainDataContainer>
                    <AkItem
                        href={href}
                        linkComponent={linkComponent}
                        elemBefore={icon}
                        onClick={onClick}
                        description={mainContentDescription}
                    >
                        {mainContent}
                    </AkItem>

                    {actions}
                </MainDataContainer>
                {this.renderDetails()}
            </CardContainer>
        );
    }

    private readonly renderDetails = () => {
        const { details, detailsSeparator } = this.props;

        if (!details) {
            return null;
        }

        return detailsSeparator === "space" ? (
            <SpaceSeparator>{details}</SpaceSeparator>
        ) : (
            <React.Fragment>
                <LineSeparator />
                {details}
            </React.Fragment>
        );
    };
}
